import React from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "./components/SolvProvider";
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormHeader,
  MainContainer
} from "./components/FormComps.js"
import {useIntlEx} from "./components/IntlUtils.js"
import ClientInviteDialog from './ClientInviteDialog.js'
import getLogger from "./components/Logging.js"
import {AccountStatusLabel, AccountTabs, useTenantPlan} from "./components/AccountUtils"
import {openDialog, openDialogCurried} from "./components/DialogUtils";
import {UserLink} from "./components/UserUtils";
import {DataTable, useCols} from "./components/TableComps";
import './App.css'
import ExportToFileDialog from "./components/ExportToFileDialog";

const log = getLogger("Clients")

export default function Clients(props) {

  const params = useParams()

  const {api, session} = useSolv()
  const {intl} = useIntlEx()

  const {tenant} = useTenant(params.tenantId)
  const {tenantPlanOpts} = useTenantPlan("CLIENT")

  const {cols} = useCols({
    specs: [
      {
        field: "tenantName",
        label: intl.msg("account"),
        filter: "text",
        sort: "ASC",
        align: "left",
      },
      {
        field: "tenantPlanId",
        label: intl.msg("account_plan"),
        filter: {
          type: "select",
          multi: true,
          searchable: false,
          options: () => tenantPlanOpts
        },
        sort: "",
        align: "left",
      },
      {
        field: "resellerTenantName",
        label: intl.msg("reseller"),
        filter: "text",
        sort: "",
        align: "left",
        visible: () => (session.user.isSystem() || tenant.hasSubTenants())
      },
      {
        field: "ownerDisplayName",
        label: intl.msg("owner"),
        filter: "text",
        sort: "",
        align: "left"
      },
      {
        field: "regionId",
        label: intl.msg("region"),
        filter: {
          type: "asyncselect",
          loader: (input, loadedOptions, additional) => {
            return api.listRegions({view: "BASIC", extraParams: `${input ? `filter=regionName:${encodeURIComponent(input)}&` : ""}sort=regionName:ASC`, cursor: additional?.cursor, limit: 20})
              .then(({data, nextCursor}) => {
                let opt = data.map(c => {
                  return {
                    value: c.regionId,
                    label: c.regionName,
                    pop: c.pop
                  };
                });
                let res = {
                  options: opt,
                }
                if (nextCursor) {
                  res = {
                    ...res,
                    hasMore: true,
                    additional: {
                      cursor: nextCursor
                    }
                  }
                }
                // log.debug("loadRegions: res=", res)
                return res
              });
          }
        },
        sort: "",
        align: "left",
      },
      {
        field: "status",
        label: intl.msg("status"),
        filter: "select:accountStatus",
        multi: true,
        sort: "",
        align: "left",
      },
    ],
    storageItemName: "clients",
  })

  function handleFetchDataPage({pageParam: cursor}) {
    return api.listClients({tenantId: tenant.tenantId, extraParams: cols.stringify(), cursor: cursor})
  }

  function handleRenderDataCol(col, row) {
    let n
    switch (col.field) {
      case "tenantName":
        n = <span>{row.tenantName}</span>
        break
      case "resellerTenantName":
        n = <div className="d-flex gap-1">
              <a className="text-link" href={`/${row.resellerTenantId}`}>
                {row.resellerTenantName}
              </a>
              {/*<span>*/}
              {/*  {tenant && row && row.resellerTenantId.replace(/\-/g, "") === tenant.tenantId.replace(/\-/g, "") ? <TagLabel label="you"/>: ""}*/}
              {/*</span>*/}
            </div>
        break
      case "ownerDisplayName":
        n = <div className="d-flex">
              {
                row.status !== "INVITING" ? (
                  <UserLink user={row.owner} session={session} tenant={row}/>
                ) : (
                  row.owner.displayName
                )
              }
            </div>
        break
      case "tenantPlanId":
        n = <span>{intl.msg(`account_plan_${row.tenantPlanId?.toLowerCase()}`)}</span>
        break
      case "regionId":
        n = <span>{row.regionName}</span>
        break
      case "industryId":
        n = <span>{row.industryName}</span>
        break
      case "status":
        n = AccountStatusLabel(row)
        break
    }
    return (
      n
    )
  }

  async function handleExport() {
    return api.exportClients({tenantId: tenant.tenantId, extraParams: cols.stringify()})
  }

  function handleRowClick(row) {
    window.location = `/${row.tenantId}`
  }

  function canAdd() {
    return tenant.isReseller() && session && (session.user.isSystemUser() || session.user.isResellerUser())
  }

  function canExport() {
    return session && (session.user.isSystemUser() || session.user.isResellerUser())
  }

  function FormControls(props) {
    const ctls = []

    if (canAdd()) {
      ctls.push(<ActionButton name="add_new" flavor="primary" onClick={() => openDialog("dlg_client_invite")}/>)
    }

    ctls.push(<MoreMenu/>)

    return ctls
  }

  function MoreMenu(props) {

    const ctls = []

    if (canExport()) {
      ctls.push(
        <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_clients_export")} prefixWithDivider={ctls.length > 0}/>
      )
    }

    return (
      ctls.length > 0 ? (
        <DropdownMenu>
          {ctls}
        </DropdownMenu>
      ) : (
        <></>
      )
    )
  }

  return (
    tenant &&
      <>
        <MainContainer tenant={tenant} menu={MainMenu} allowIfAccessingAs={["*/*/RESELLER", "*/*/SYSTEM"]}>
          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {intl.msg("clients")}
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <FormControls/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
          </FormHeader>
          <FormBody>
            <DataTable
              tenant={tenant}
              cols={cols}
              resourceName="clients"
              dataKey="tenantId"
              onFetchDataPage={handleFetchDataPage}
              onRenderDataCol={handleRenderDataCol}
              onClickDataRow={handleRowClick}
            />
          </FormBody>
        </MainContainer>
        <ClientInviteDialog
          tenant={tenant}
        />
        <ExportToFileDialog
          key="download"
          type="clients"
          fileName={`solv-clients`}
          fileExt={"csv"}
          onExport={handleExport}/>
      </>
   )
}